import { Header, Icon, Message, Table } from "semantic-ui-react"
import { getResourceFilter } from "../../../utils/reportResourceUtil"
import { MetadataField, ReportField } from "../../../constnats/reportConstants"
import ProvenanceValueCell from "../../provenanceValueCell"
import { PROVENANCE_PERSPECTIVE } from "../../../constnats/review"

// Marks the descriptor apart from the pale red Semantic paints across the rest of the row.
const MANUALLY_CALCULATED_CELL_CLASS = 'review__manually-calculated-cell'

function ReportsDiff({ schemaErrors, reportDiffs, editName, reviewName, detailed, companyId, summarized, heading, restricted, sourceViewEnabled, getProvenance, onValueClick, perspective }) {
    const reportsDiff = groupByReportAndDescriptor(reportDiffs, detailed, summarized)

    const getBody = (reportsDiff, schemaErrors) => {
        let body
        if (schemaErrors === false && reportsDiff.length === 0) {
            body = <Message success attached='bottom'>
                No differences found. </Message>
        } else {
            body = <>
                {schemaErrors &&
                    <Message warning attached='bottom'>
                        <Icon name='warning' />
                        The report differences may not be calculated with 100% beacause of Model differences!
                    </Message>
                }
                <Table celled structured selectable>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>No</Table.HeaderCell>
                        <Table.HeaderCell>Report</Table.HeaderCell>
                        {(summarized || detailed) &&
                            <Table.HeaderCell>Field Label</Table.HeaderCell>
                        }
                        {detailed && <>
                            <Table.HeaderCell>Property</Table.HeaderCell>
                            <Table.HeaderCell>{editName} branch</Table.HeaderCell>
                            <Table.HeaderCell>{reviewName} branch</Table.HeaderCell>
                        </>}
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {reportsDiff.map(({ descriptorGroups, totalRowCount }, reportIdx) =>
                        descriptorGroups.map(({ key, fieldLabel, diffs, manuallyCalculated }, groupIdx) =>
                            diffs.map((diff, diffIdx) => {
                                const valueCellClasses = getValueCellClasses(diff.property)
                                return <Table.Row key={`${key}|${diff.property}`} error={diff.type === "ERROR"} warning={diff.type === "WARNING"}>
                                    {groupIdx === 0 && diffIdx === 0 && <>
                                        <Table.Cell rowSpan={totalRowCount} >{reportIdx + 1}</Table.Cell>
                                        <Table.Cell rowSpan={totalRowCount} >
                                            <ReportCellContent companyId={companyId} reportName={diff.name} />
                                        </Table.Cell>
                                    </>}
                                    {diffIdx === 0 && <Table.Cell rowSpan={diffs.length}
                                        className={manuallyCalculated ? MANUALLY_CALCULATED_CELL_CLASS : undefined}>
                                        <DescriptorLabelCellContent
                                            fieldLabel={fieldLabel}
                                            provenance={getDescriptorProvenance(diffs[0], perspective, getProvenance)}
                                            restricted={restricted}
                                            sourceViewEnabled={sourceViewEnabled}
                                            onValueClick={onValueClick}
                                        />
                                    </Table.Cell>}
                                    {detailed && <>
                                        <Table.Cell>{diff.property}</Table.Cell>
                                        <Table.Cell className={valueCellClasses}>
                                            {getDisplayValue(diff.edit, diff.property, restricted)}
                                        </Table.Cell>
                                        <Table.Cell className={valueCellClasses}>
                                            {getDisplayValue(diff.review, diff.property, restricted)}
                                        </Table.Cell>
                                    </>}
                                </Table.Row>
                            })
                        )
                    )}
                </Table.Body>
            </Table>
            </>
        }

        return body
    }

    return <>
        <Header as="h5">{heading || "Report Differences"}</Header>
        {getBody(reportsDiff, schemaErrors)}
    </>
}

// One descriptor produces one diff per differing property (the value plus each metadata prop), so
// the rows are grouped a second time, by descriptor, to let a descriptor span its property rows
// instead of repeating its label on every one of them.
//
// ReportDiffUtil emits a descriptor's diffs consecutively, so grouping in insertion order leaves
// the rows in the order they were calculated.
function groupByReportAndDescriptor(reportDiffs, detailed, summarized) {
    const reports = new Map()

    for (const diff of reportDiffs) {
        if (!reports.has(diff.name)) {
            reports.set(diff.name, new Map())
        }

        const descriptors = reports.get(diff.name)
        const key = summarized ? diff.fieldLabel : getDescriptorKey(diff)

        if (!descriptors.has(key)) {
            if (detailed || summarized) {
                descriptors.set(key, { key, fieldLabel: diff.fieldLabel, diffs: [diff] })
            }
        } else if (detailed) {
            // Summarized keeps the first diff only — the label is what it reports, not the property.
            descriptors.get(key).diffs.push(diff)
        }

        // Calculating a field by hand is a property of the descriptor rather than of the one row that
        // reports it, so it is noted on the group — which is also how it survives summarized dropping
        // every diff of a descriptor but the first. No group exists when the table has no Field Label
        // column to mark.
        const group = descriptors.get(key)
        if (group && diff.property === MetadataField.MANUALLY_CALCED) {
            group.manuallyCalculated = true
        }
    }

    return [...reports.values()].map(descriptors => {
        const descriptorGroups = [...descriptors.values()]
        return {
            descriptorGroups,
            totalRowCount: descriptorGroups.reduce((total, group) => total + group.diffs.length, 0),
        }
    })
}

// Descriptors are keyed by their ids as well as their label so that two distinct descriptors that
// happen to share a label are not merged into one group. Every diff of a descriptor pair carries
// the same id pair; either id is unset when that side has no descriptor to address.
function getDescriptorKey(diff) {
    return `${diff.editDescriptorId ?? ""}|${diff.reviewDescriptorId ?? ""}|${diff.fieldLabel}`
}

// The descriptor's label is the one cell that stands for the whole descriptor, so it is where a
// source is inspected from. Provenance is recorded per descriptor per report — the value and the
// metadata describing it (reported-as label, src, correction, ...) all trace back to the same region
// of the same document — so a link on every property row would repeat one piece of information.
function DescriptorLabelCellContent({ fieldLabel, provenance, restricted, sourceViewEnabled, onValueClick }) {
    return <ProvenanceValueCell
        value={fieldLabel}
        provenance={provenance}
        clickable={isLabelClickable(provenance, restricted, sourceViewEnabled)}
        onClick={onValueClick}
    />
}

// The perspective picks which side of the diff the source is read from. Every diff of a descriptor
// group carries the same id pair (see getDescriptorKey), so any of them answers for the group.
// Either id is unset when that side has no report or no descriptor to address, and the lookup
// returns undefined — the label then renders as plain text.
function getDescriptorProvenance(diff, perspective, getProvenance) {
    return perspective === PROVENANCE_PERSPECTIVE.REVIEW
        ? getProvenance(diff.reviewReportId, diff.reviewDescriptorId)
        : getProvenance(diff.editReportId, diff.editDescriptorId)
}

// A label is inspectable when source view is on, the user is not restricted, and provenance was
// recorded for the descriptor on the selected side.
function isLabelClickable(provenance, restricted, sourceViewEnabled) {
    return !!sourceViewEnabled && !restricted && !!provenance?.length
}

function getValueCellClasses (property) {
    let classes = "";
    if(property === "value") {
        classes = `${classes} blocked-events`
    }
    if (property === "value" || property == "N/A" || property == "ALL") {
        classes = `${classes} coreValueDiff`
    }
    return classes
}

function getDisplayValue(value, property, restricted) {
    if (restricted && (property === "value" || property === "ALL")) {
        return "Unavailable"
    }
    return String(value)
}


function ReportCellContent({ reportName, companyId }) {
    const [year, period, version] = reportName.split("/")
    const filter = getResourceFilter({ [ReportField.YEAR]: year, [ReportField.PERIOD]: period, [ReportField.TYPE]: version }, companyId)
    return <a target="_blank" rel="noopener noreferrer"
        href={"/resources/preview/" + btoa(JSON.stringify(filter))}>
        {reportName}
    </a>
}


export default ReportsDiff